import type {
  OpenApiServer,
  OpenApiSpec,
  Swagger2Spec,
} from '../types/index';

const API_PATH_SUFFIX = '/api';

const removeTrailingSlash = (value: string): string =>
  value.endsWith('/') ? value.slice(0, -1) : value;

const isAbsoluteUrl = (value: string): boolean =>
  /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(value);

export const normalizeApiOrigin = (baseUrl: string): string => {
  const parsedUrl = new URL(baseUrl);
  const trimmedPathname = parsedUrl.pathname.replace(/\/+$/, '');
  const normalizedPathname = trimmedPathname.endsWith(API_PATH_SUFFIX)
    ? trimmedPathname.slice(0, -API_PATH_SUFFIX.length)
    : trimmedPathname;

  parsedUrl.pathname = normalizedPathname || '/';
  parsedUrl.search = '';
  parsedUrl.hash = '';

  return removeTrailingSlash(parsedUrl.toString());
};

export const buildApiSpecUrl = (baseUrl: string, isDevelopment: boolean): string => {
  if (isDevelopment) {
    return '/api/proxy/apispec';
  }

  return `${normalizeApiOrigin(baseUrl)}/apispec_1.json`;
};

export const normalizeOpenApiServers = (spec: OpenApiSpec, baseUrl: string): OpenApiSpec => {
  const normalizedApiOrigin = normalizeApiOrigin(baseUrl);
  const fallbackServer: OpenApiServer = { url: normalizedApiOrigin };

  if (!Array.isArray(spec.servers) || spec.servers.length === 0) {
    return { ...spec, servers: [fallbackServer] };
  }

  const normalizedServers = spec.servers.map((server) => {
    if (!server || typeof server.url !== 'string' || server.url.trim().length === 0) {
      return fallbackServer;
    }

    if (isAbsoluteUrl(server.url)) {
      return {
        ...server,
        url: removeTrailingSlash(server.url),
      };
    }

    const resolvedServerUrl = new URL(server.url, `${normalizedApiOrigin}/`).toString();

    return {
      ...server,
      url: removeTrailingSlash(resolvedServerUrl),
    };
  });

  return {
    ...spec,
    servers: normalizedServers,
  };
};

const isSwagger2Spec = (spec: OpenApiSpec): spec is Swagger2Spec => {
  const swaggerVersion = (spec as Swagger2Spec).swagger;
  return typeof swaggerVersion === 'string' && swaggerVersion.startsWith('2.');
};

export const normalizeSwagger2Host = (spec: Swagger2Spec, baseUrl: string): Swagger2Spec => {
  const normalizedApiOrigin = normalizeApiOrigin(baseUrl);
  const parsedOrigin = new URL(normalizedApiOrigin);

  return {
    ...spec,
    host: parsedOrigin.host,
    schemes: [parsedOrigin.protocol.replace(':', '')],
  };
};

export const normalizeApiSpecForDocs = (spec: OpenApiSpec, baseUrl: string): OpenApiSpec => {
  if (isSwagger2Spec(spec)) {
    return normalizeSwagger2Host(spec, baseUrl);
  }

  return normalizeOpenApiServers(spec, baseUrl);
};

export const isJsonContentType = (contentType: string | null): boolean => {
  if (!contentType) {
    return false;
  }

  const normalizedValue = contentType.toLowerCase();
  return normalizedValue.includes('application/json') || normalizedValue.includes('+json');
};
